import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { NmLoadingOverlay } from './NmLoadingOverlay';
import type { CalendarUploadProgress } from '../utils/calendarFileDrop';

interface CalendarUploadOverlayProps {
  progress: CalendarUploadProgress;
}

/** Full-page frosted overlay while calendar files upload / import / analyze. */
export function CalendarUploadOverlay({ progress }: CalendarUploadOverlayProps) {
  const { t } = useTranslation();

  let message = t('calendar.upload.uploading');
  let detail: string | undefined;
  let uploadPercent: number | undefined;

  if (progress.phase === 'uploading') {
    message = t('calendar.upload.uploading');
    if (progress.fileTotal && progress.fileTotal > 1 && progress.fileIndex) {
      detail = t('calendar.upload.fileProgress', {
        current: progress.fileIndex,
        total: progress.fileTotal,
      });
    } else if (progress.fileName) {
      detail = progress.fileName;
    } else {
      detail = t('calendar.upload.uploadWait');
    }
    uploadPercent = progress.uploadPercent;
  } else if (progress.phase === 'processing') {
    message = t('calendar.upload.processing');
    detail = t('calendar.upload.processingDetail', {
      current: progress.processingIndex ?? 1,
      total: progress.processingTotal ?? 1,
    });
  } else if (progress.phase === 'health_analyzing') {
    message = t('healthDocument.state.analyzing');
    detail = progress.fileName ?? t('healthDocument.state.processing');
  }

  const overlay = (
    <NmLoadingOverlay
      variant="page"
      message={message}
      detail={detail}
      progress={uploadPercent}
      style={{ zIndex: 14000 }}
    />
  );

  if (typeof document === 'undefined') {
    return overlay;
  }

  return createPortal(overlay, document.body);
}
